// Human-readable summary of a strategy's spec: indicators (with params) + the long/short
// entry expressions resolved to friendly condition names (not raw indices). Used on the
// Strategies list rows and the Monitor page so you can see WHAT a strategy actually does
// without opening the Builder.
//
// Condition names come from POST /Conditions/preview (the same catalog the Builder uses),
// so the index→name mapping is always consistent with what the engine evaluates. Degrades
// gracefully to raw indices if the catalog can't be fetched.

import { useEffect, useState } from "react";
import type { StrategySpecDto } from "../../api/types";
import { previewConditions, type ConditionPreviewEntry } from "../../api/conditions";
import { COLORS } from "../shell/theme";

interface Props {
  spec: StrategySpecDto;
  /** Compact mode for dense list rows (smaller text, single-line indicators). */
  compact?: boolean;
}

export function StrategySpecSummary({ spec, compact = false }: Props) {
  const [conditions, setConditions] = useState<ConditionPreviewEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  // One-shot fetch — the spec is static here (no debounce needed, unlike the Builder).
  useEffect(() => {
    if (spec.indicatorSpecs.length === 0) { setConditions([]); return; }
    const ac = new AbortController();
    setFailed(false);
    previewConditions(
      { indicatorSpecs: spec.indicatorSpecs, useAdvanced: spec.useAdvanced, usePriceAction: spec.usePriceAction },
      ac.signal,
    )
      .then((c) => setConditions(c))
      .catch((e) => { if ((e as Error).name !== "AbortError") setFailed(true); });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(spec.indicatorSpecs), spec.useAdvanced, spec.usePriceAction]);

  const fontSize = compact ? 11 : 12;

  const expr = (indices: readonly number[]): { text: string; resolved: boolean } => {
    if (indices.length === 0) return { text: "—", resolved: true };
    if (!conditions) {
      // Loading or failed → show raw indices so the row is never blank/misleading.
      return { text: indices.map((i) => `[${i}]`).join(" ∧ "), resolved: false };
    }
    const byIdx = new Map(conditions.map((c) => [c.index, c]));
    return {
      text: indices.map((i) => byIdx.get(i)?.name ?? `[${i}]`).join("  ∧  "),
      resolved: true,
    };
  };

  const longExpr = expr(spec.longCondIndices);
  const shortExpr = expr(spec.shortCondIndices);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize }}>
      {/* Indicators */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        <span style={labelStyle}>Indicators</span>
        {spec.indicatorSpecs.length === 0 ? (
          <span style={{ color: COLORS.textMuted }}>none</span>
        ) : (
          spec.indicatorSpecs.map((ind) => (
            <span key={ind.instanceKey} style={chipStyle}>
              <strong style={{ color: COLORS.text }}>{ind.baseId}</strong>
              <span style={{ color: COLORS.textMuted }}>
                {" "}({Object.entries(ind.params).map(([k, v]) => `${k}=${v}`).join(", ") || "default"})
              </span>
            </span>
          ))
        )}
        <span style={{ color: COLORS.textMuted }}>
          · exit {spec.exitMode}/{spec.forwardBars}
        </span>
      </div>

      {/* Long / short expressions */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        <ExprLine label="Long" color={COLORS.green} text={longExpr.text} />
        <ExprLine label="Short" color={COLORS.red} text={shortExpr.text} />
      </div>

      {(failed || (!longExpr.resolved && spec.indicatorSpecs.length > 0)) && (
        <div style={{ fontSize: 10, color: COLORS.amber }}>
          {failed
            ? "condition names unavailable (HistoricalIndicators unreachable) — showing raw indices"
            : "resolving condition names…"}
        </div>
      )}
    </div>
  );
}

function ExprLine({ label, color, text }: { label: string; color: string; text: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
      <span style={{ ...labelStyle, color }}>{label}</span>
      <code style={{ color: text === "—" ? COLORS.textMuted : COLORS.text, fontFamily: "monospace" }}>
        {text}
      </code>
    </span>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: COLORS.textMuted,
};

const chipStyle: React.CSSProperties = {
  background: COLORS.bgRaised,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 3,
  padding: "1px 6px",
  fontFamily: "monospace",
};
